import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useAuthSubscriber } from "./Services/authentication";
import useLocations from "./CustomHooks/useLocations";
import useComplaints from "./CustomHooks/useComplaints";
import useScreenStack from "./CustomHooks/useScreenStack";
import { getComplaintsCount } from "./Redux/complaints/action";
import { getWorkforceCount } from "./Redux/locations/action";
import { toTimestamps, startOfToday, DEFAULT_RANGE_TYPE } from "./Utils/dateRange";
import Header from "./Components/Header/Header";
import ProfileMenu from "./Components/ProfileMenu/ProfileMenu";
import Login from "./Screens/Login/Login";
import Overview from "./Screens/Overview/Overview";
import Zone from "./Screens/Zone/Zone";
import Ward from "./Screens/Ward/Ward";
import Location from "./Screens/Location/Location";
import LocationComplaints from "./Screens/Location/LocationComplaints";
import LocationProcedures from "./Screens/Location/LocationProcedures";
import HomeComplaints from "./Screens/Overview/HomeComplaints";
import HomeProcedures from "./Screens/Overview/HomeProcedures";
import ComplaintDetail from "./Screens/Complaint/ComplaintDetail";
import ProcedureDetail from "./Screens/Procedure/ProcedureDetail";
import HomeWorkforce from "./Screens/Workforce/HomeWorkforce";
import EmployeeDetail from "./Screens/Workforce/EmployeeDetail";
import "./App.css";

function getScreenClass(id, stack) {
  const idx = stack.indexOf(id);
  if (idx === -1) return "screen";
  if (idx === stack.length - 1) return "screen active";
  if (idx === stack.length - 2) return "screen behind";
  return "screen prev";
}

function App({ accessToken, userEmail, locationsData, allowedZones, complaintsCount }) {
  const allowedLocationIds = locationsData ? Object.keys(locationsData) : null;
  const [isAuth, setIsAuth] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedWard, setSelectedWard] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  // Single date-range value shared by the home Operations bar and every
  // individual Location screen, so drilling into a location keeps whatever
  // range was picked on the home screen (and vice versa).
  const [range, setRange] = useState({ type: DEFAULT_RANGE_TYPE, from: startOfToday(), to: Date.now() });

  const { screenStack, overlay, pushScreen, openOverlay, goBack, resetNav } = useScreenStack();

  const handleRangeChange = ({ type, from: customFrom, to: customTo }) => {
    if (type === "custom" && (!customFrom || !customTo)) {
      setRange((prev) => ({ ...prev, type: "custom" }));
      return;
    }
    const { from, to } = toTimestamps(type, customFrom, customTo);
    setRange({ type, from, to });
    getComplaintsCount(from, to, allowedZones);
    getWorkforceCount(from, to, allowedZones);
  };

  const handleAuthChange = useCallback(({ isAuth: auth }) => {
    setIsAuth(Boolean(auth));
  }, []);

  useAuthSubscriber(handleAuthChange);
  useLocations({ isAuth, allowedZones });
  useComplaints({ isAuth, allowedZones });

  const loggedOut = !isAuth && !accessToken;

  useEffect(() => {
    if (loggedOut) resetNav();
  }, [loggedOut, resetNav]);

  const userInitials = userEmail
    ? userEmail.slice(0, 2).toUpperCase()
    : "—";

  if (loggedOut) {
    return <Login />;
  }

  return (
    <div id="app">
      <Header
        userInitials={userInitials}
        onAvatarClick={() => (overlay === "profile" ? goBack() : openOverlay("profile"))}
      />
      <ProfileMenu
        isOpen={overlay === "profile"}
        onClose={goBack}
      />
      <div className="sw">
        <Overview
          className={getScreenClass("overview", screenStack)}
          range={range}
          onRangeChange={handleRangeChange}
          onZoneClick={(zone) => {
            setSelectedZone(zone);
            pushScreen("zone");
          }}
          onComplaintsClick={() => pushScreen("home-complaints")}
          onProceduresClick={() => pushScreen("home-procedures")}
          onWorkforceClick={() => pushScreen("workforce")}
        />
        <Zone
          className={getScreenClass("zone", screenStack)}
          zone={selectedZone}
          locationsData={locationsData}
          onBack={goBack}
          onWardClick={(ward) => {
            setSelectedWard(ward);
            pushScreen("ward");
          }}
        />
        <Ward
          className={getScreenClass("ward", screenStack)}
          zone={selectedZone}
          ward={selectedWard}
          locationsData={locationsData}
          onBack={goBack}
          onLocationClick={(id) => {
            setSelectedLocation(id);
            pushScreen("location");
          }}
        />
        <Location
          className={getScreenClass("location", screenStack)}
          location={locationsData?.[selectedLocation]}
          zone={selectedZone}
          ward={selectedWard}
          range={range}
          onRangeChange={handleRangeChange}
          onBack={goBack}
          onComplaintsClick={() => pushScreen("location-complaints")}
          onProceduresClick={() => pushScreen("location-procedures")}
        />
        <LocationComplaints
          className={getScreenClass("location-complaints", screenStack)}
          location={locationsData?.[selectedLocation]}
          zone={selectedZone}
          ward={selectedWard}
          range={range}
          onBack={goBack}
          onComplaintClick={(c) => { setSelectedComplaint(c); pushScreen("complaint-detail"); }}
        />
        <LocationProcedures
          className={getScreenClass("location-procedures", screenStack)}
          location={locationsData?.[selectedLocation]}
          zone={selectedZone}
          ward={selectedWard}
          onBack={goBack}
          onProcedureClick={(p) => { setSelectedProcedure(p); pushScreen("procedure-detail"); }}
        />
        <HomeComplaints
          className={getScreenClass("home-complaints", screenStack)}
          onBack={goBack}
          onComplaintClick={(c) => { setSelectedComplaint(c); pushScreen("complaint-detail"); }}
          allowedZones={allowedZones}
          complaintsCount={complaintsCount}
          range={range}
        />
        <HomeProcedures
          className={getScreenClass("home-procedures", screenStack)}
          onBack={goBack}
          onProcedureClick={(p) => { setSelectedProcedure(p); pushScreen("procedure-detail"); }}
          allowedZones={allowedZones}
        />
        <ComplaintDetail
          className={getScreenClass("complaint-detail", screenStack)}
          complaint={selectedComplaint}
          onBack={goBack}
          imageOverlayOpen={overlay === "complaint-image"}
          onOpenImageOverlay={() => openOverlay("complaint-image")}
        />
        <ProcedureDetail
          className={getScreenClass("procedure-detail", screenStack)}
          procedure={selectedProcedure}
          onBack={goBack}
          imageOverlayOpen={overlay === "procedure-image"}
          onOpenImageOverlay={() => openOverlay("procedure-image")}
        />
        <HomeWorkforce
          className={getScreenClass("workforce", screenStack)}
          onBack={goBack}
          onEmployeeClick={(emp) => { setSelectedEmployee(emp); pushScreen("employee-detail"); }}
          allowedLocationIds={allowedLocationIds}
        />
        <EmployeeDetail
          className={getScreenClass("employee-detail", screenStack)}
          employee={selectedEmployee}
          onBack={goBack}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  accessToken: state.auth.data.accessToken,
  userEmail: state.auth.data.email,
  locationsData: state.locations.data,
  allowedZones: state.auth.data.zones,
  complaintsCount: state.complaints.complaintsCount,
});

export default connect(mapStateToProps)(App);
